import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { TenantProvider } from "./contexts/TenantContext";
import { Toaster } from "./components/ui/sonner";

// Layout
import MainLayout from "./components/layout/MainLayout";

// Pages
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import CashierDashboardPage from "./pages/CashierDashboardPage";
import CampusCorrespondentPage from "./pages/CampusCorrespondentPage";
import InstrumentationOpsPage from "./pages/InstrumentationOpsPage";
import DashboardPage from "./pages/DashboardPage";
import SuperDashboardPage from "./pages/SuperDashboardPage";
import EntitiesPage from "./pages/EntitiesPage";
import CampusesPage from "./pages/CampusesPage";
import CampusDetailPage from "./pages/CampusDetailPage";
import StoresPage from "./pages/StoresPage";
import StoreDetailPage from "./pages/StoreDetailPage";
import CashiersPage from "./pages/CashiersPage";
import CashierFloatLogsPage from "./pages/CashierFloatLogsPage";
import CashierPaymentOrdersPage from "./pages/CashierPaymentOrdersPage";
import PaymentOrdersOverviewPage from "./pages/PaymentOrdersOverviewPage";
import CardCentrePage from "./pages/CardCentrePage";
import UsersPage from "./pages/UsersPage";
import SubscribersPage from "./pages/SubscribersPage";
import SubscriberDetailPage from "./pages/SubscriberDetailPage";
import CardRenewalsPage from "./pages/CardRenewalsPage";
import EcoPlusManagePage from "./pages/MobileApps/EcoPlusManagePage";
import EcoFrontManagePage from "./pages/MobileApps/EcoFrontManagePage";
import WalletTransactionsPage from "./pages/WalletTransactionsPage";
import InterWalletsPage from "./pages/InterWalletsPage";
import MessagingPage from "./pages/MessagingPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import ConfigurationsPage from "./pages/ConfigurationsPage";
import TimezonePage from "./pages/TimezonePage";
import AuditTrailPage from "./pages/AuditTrailPage";
import DocumentationPage from "./pages/DocumentationPage";
import FloatLedgerPage from "./pages/FloatLedgerPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";

// Customer Experience workspace
import CxWorkspace from "./pages/cx/CxWorkspace";

// Public Customer Hub (QR landing) + admin config
import PublicHubPage from "./pages/PublicHubPage";
import CustomerHubPage from "./pages/CustomerHubPage";
import CustomerHubFormsPage from "./pages/CustomerHubFormsPage";
import BillingPage from "./pages/BillingPage";

// Onboarding
import OnboardingProvider from "./components/onboarding/OnboardingProvider";

// Smart redirect based on user role
function RoleBasedRedirect() {
  const { user, loading, mustChangePassword } = useAuth();

  if (loading) {
    return <div className="min-h-screen bg-[#FCFAF6] flex items-center justify-center">Loading...</div>;
  }

  if (mustChangePassword) {
    return <Navigate to="/change-password" replace />;
  }

  // Super Admin goes to Super Dashboard, Cashier goes to Cashier Dashboard, others go to Dashboard
  if (user?.role === 'super_admin') {
    return <Navigate to="/super-dashboard" replace />;
  }
  if (user?.role === 'campus_agent') {
    return <Navigate to="/cashier-app" replace />;
  }
  if (user?.role === 'campus_correspondent') {
    return <Navigate to="/campus" replace />;
  }
  if (user?.role === 'instrumentation_ops') {
    return <Navigate to="/instrumentation" replace />;
  }
  if (user?.role === 'cx_agent') {
    return <Navigate to="/cx" replace />;
  }

  return <Navigate to="/dashboard" replace />;
}

// Auth guard for standalone routes
function ProtectedRoute({ children, allowPasswordReset = false }) {
  const { isAuthenticated, loading, mustChangePassword } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#FCFAF6] flex items-center justify-center">Loading...</div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (mustChangePassword && !allowPasswordReset) return <Navigate to="/change-password" replace />;
  return children;
}

// Guard for sidebar-based routes. Cashiers and Campus Correspondents have
// their own standalone UIs and must NOT reach any MainLayout page even via
// the browser back button or a direct URL paste.
function SidebarOnlyRoute({ children }) {
  const { user, isAuthenticated, loading, mustChangePassword } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#FCFAF6] flex items-center justify-center">Loading...</div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (mustChangePassword) return <Navigate to="/change-password" replace />;
  if (user?.role === 'campus_agent') return <Navigate to="/cashier-app" replace />;
  if (user?.role === 'campus_correspondent') return <Navigate to="/campus" replace />;
  if (user?.role === 'instrumentation_ops') return <Navigate to="/instrumentation" replace />;
  if (user?.role === 'cx_agent') return <Navigate to="/cx" replace />;
  return children;
}

// Guards for the role-specific standalone routes — bounce any other role to
// their canonical landing page so URLs cannot be guessed or shared cross-role.
function CashierOnlyRoute({ children }) {
  const { user, isAuthenticated, loading, mustChangePassword } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#FCFAF6] flex items-center justify-center">Loading...</div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (mustChangePassword) return <Navigate to="/change-password" replace />;
  if (user?.role !== 'campus_agent') return <Navigate to="/" replace />;
  return children;
}

function CorrespondentOnlyRoute({ children }) {
  const { user, isAuthenticated, loading, mustChangePassword } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#FCFAF6] flex items-center justify-center">Loading...</div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (mustChangePassword) return <Navigate to="/change-password" replace />;
  if (user?.role !== 'campus_correspondent') return <Navigate to="/" replace />;
  return children;
}

// Customer Experience workspace — cx_agent's home (no sidebar); also reachable
// by super_admin & entity_admin. Any other role is bounced to their landing.
function CxRoute({ children }) {
  const { user, isAuthenticated, loading, mustChangePassword } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#FCFAF6] flex items-center justify-center">Loading...</div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (mustChangePassword) return <Navigate to="/change-password" replace />;
  if (!['cx_agent', 'super_admin', 'entity_admin'].includes(user?.role)) return <Navigate to="/" replace />;
  return children;
}

function InstrumentationOnlyRoute({ children }) {
  const { user, isAuthenticated, loading, mustChangePassword } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#FCFAF6] flex items-center justify-center">Loading...</div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (mustChangePassword) return <Navigate to="/change-password" replace />;
  if (user?.role !== 'instrumentation_ops') return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <TenantProvider>
          <OnboardingProvider>
            <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/hub/:slug" element={<PublicHubPage />} />

            {/* Password reset (allowed while must_change_password=true) */}
            <Route path="/change-password" element={<ProtectedRoute allowPasswordReset><ChangePasswordPage /></ProtectedRoute>} />

            {/* Cashier standalone route (no sidebar, cashier-only) */}
            <Route path="/cashier-app" element={<CashierOnlyRoute><CashierDashboardPage /></CashierOnlyRoute>} />

            {/* Campus Correspondent standalone route (no sidebar, correspondent-only) */}
            <Route path="/campus" element={<CorrespondentOnlyRoute><CampusCorrespondentPage /></CorrespondentOnlyRoute>} />

            {/* Instrumentation Operations standalone route (no sidebar) */}
            <Route path="/instrumentation" element={<InstrumentationOnlyRoute><InstrumentationOpsPage /></InstrumentationOnlyRoute>} />

            {/* Customer Experience standalone workspace (no sidebar) — single page with tabs */}
            <Route path="/cx" element={<CxRoute><CxWorkspace /></CxRoute>} />
            
            {/* Protected Routes (sidebar UI — blocked for cashier & campus_correspondent) */}
            <Route element={<SidebarOnlyRoute><MainLayout /></SidebarOnlyRoute>}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/super-dashboard" element={<SuperDashboardPage />} />
              <Route path="/entities" element={<EntitiesPage />} />
              <Route path="/campuses" element={<CampusesPage />} />
              <Route path="/campuses/:id" element={<CampusDetailPage />} />
              <Route path="/stores" element={<StoresPage />} />
              <Route path="/stores/:id" element={<StoreDetailPage />} />
              <Route path="/cashiers" element={<CashiersPage />} />
              <Route path="/cashiers/float-logs" element={<CashierFloatLogsPage />} />
              <Route path="/cashiers/payment-orders" element={<CashierPaymentOrdersPage />} />
              <Route path="/cashiers/payment-orders/overview" element={<PaymentOrdersOverviewPage />} />
              <Route path="/card-centre" element={<CardCentrePage />} />
              <Route path="/customer-hub" element={<CustomerHubPage />} />
              <Route path="/customer-hub/forms" element={<CustomerHubFormsPage />} />
              <Route path="/billing" element={<BillingPage />} />
              <Route path="/users" element={<UsersPage />} />
              <Route path="/subscribers" element={<SubscribersPage />} />
              <Route path="/subscribers/:id" element={<SubscriberDetailPage />} />
              <Route path="/card-renewals" element={<CardRenewalsPage />} />
              <Route path="/mobile-apps/ecoplus" element={<EcoPlusManagePage />} />
              <Route path="/mobile-apps/ecofront" element={<EcoFrontManagePage />} />
              <Route path="/wallet-transactions" element={<WalletTransactionsPage />} />
              <Route path="/wallet-transactions/inter-wallets" element={<InterWalletsPage />} />
              <Route path="/messaging" element={<MessagingPage />} />
              <Route path="/analytics" element={<AnalyticsPage />} />
              <Route path="/configurations" element={<ConfigurationsPage />} />
              <Route path="/timezone" element={<TimezonePage />} />
              <Route path="/audit-trail" element={<AuditTrailPage />} />
              <Route path="/float-ledger" element={<FloatLedgerPage />} />
              <Route path="/documentation" element={<DocumentationPage />} />
            </Route>
            
            {/* Redirect root based on role */}
            <Route path="/" element={<RoleBasedRedirect />} />
            
            {/* Catch all - redirect based on role */}
            <Route path="*" element={<RoleBasedRedirect />} />
          </Routes>
          <Toaster position="top-right" richColors />
          </OnboardingProvider>
        </TenantProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
